import { FieldType } from 'types';
import {
  CO_PAYMENT_OPTIONS,
  FREQUENCY_OPTIONS,
  PAYMENT_METHODS_OPTIONS,
  PAYOUT_BENEFIT_OPTIONS,
  PAYOUT_FREQUENCY_OPTIONS,
  PAYOUT_WAIT_OPTIONS,
} from './options';
import { INSURANCE_TYPES_NAMES } from './insurance-types-names';

export const RENEWAL_START_DAYS = 60;

export const RENEWAL_FIELD = {
  basic: true,
  key: 'renewalDate',
  label: 'Renewal date',
  type: FieldType.Birthday,
  max: new Date().getFullYear() + 2,
  min: 'TODAY',
  required: true,
};

export const TRAVEL_START_DATE_FIELD = {
  basic: true,
  key: 'startDate',
  label: 'Start date',
  type: FieldType.Date,
  max: undefined,
};

export const TRAVEL_END_DATE_FIELD = {
  ...RENEWAL_FIELD,
  label: 'End date',
  type: FieldType.Date,
  max: undefined,
};

export const ADDRESS_FIELD = {
  basic: true,
  key: 'address',
  label: 'Insured address',
  placeholder: 'Enter address',
  type: FieldType.Address,
};

export const ADDRESS_QUESTION_REQUIRED = {
  ...ADDRESS_FIELD,
  required: true,
};

export const REGO_FIELD = {
  basic: true,
  key: 'numberPlate',
  label: 'Plate number',
  maxLength: 6,
  placeholder: 'Enter plate number',
  validator: 'rego',
  type: FieldType.Text,
  required: true,
};

export const NAME_FIELD = {
  basic: true,
  key: 'name',
  label: 'Insured name',
  placeholder: 'Enter name',
  type: FieldType.Text,
  required: true,
};

export const VESSEL_FIELD = {
  basic: true,
  key: 'vessel',
  label: 'Insured vessel',
  placeholder: 'Enter vessel',
  type: FieldType.Text,
  required: true,
};

export const PET_NAME_FIELD = {
  basic: true,
  key: 'petName',
  label: 'Pet name',
  placeholder: 'Enter name',
  type: FieldType.Text,
  required: true,
};

export const POLICY_SUB_TYPE_FIELD = {
  basic: true,
  key: 'policySubTypeId',
  label: 'Policy type',
  options: 'policySubTypeId',
  placeholder: 'Select policy type',
  type: FieldType.Select,
  valueKey: 'policySubTypeId',
};

export const POLICY_NUMBER_QUESTION = {
  basic: true,
  key: 'policyNumber',
  label: 'Policy number',
  placeholder: 'Enter policy number',
  type: FieldType.Text,
};

export const USER_ID_QUESTION = {
  basic: true,
  key: 'userId',
  label: 'User ID',
  placeholder: 'Enter user ID',
  type: FieldType.Text,
  display: [{ when: 'isAdmin', is: true }],
};

export const PREMIUM_QUESTION = {
  basic: true,
  decimalScale: 2,
  key: 'premium',
  label: 'Premium',
  placeholder: 'Enter amount',
  prefix: '$',
  type: FieldType.Number,
  required: true,
  confirmation: 'This looks incorrect, are you sure?',
};

export const PREMIUM_WITH_WARNING_QUESTION = {
  ...PREMIUM_QUESTION,
  warning: {
    is: true,
    text: 'Your premium looks high, are you sure this is correct?',
    when: 'highPremium',
  },
};

export const FREQUENCY_QUESTION = {
  basic: true,
  key: 'frequency',
  label: 'Frequency',
  placeholder: 'Select frequency',
  options: FREQUENCY_OPTIONS,
  type: FieldType.Select,
  required: true,
};

export const AMOUNT_QUESTION = {
  basic: true,
  key: 'amount',
  label: 'Insured amount',
  placeholder: 'Enter amount',
  prefix: '$',
  type: FieldType.Number,
};

export const COVER_LIMIT_QUESTION = {
  basic: true,
  key: 'coverLimit',
  label: 'Claim limit',
  placeholder: 'Enter cover limit',
  type: FieldType.Text,
};

export const EXCESS_QUESTION = {
  basic: true,
  creatable: true,
  inputType: FieldType.Number,
  key: 'excess',
  label: 'Excess',
  placeholder: 'Enter excess',
  prefix: '$',
  type: FieldType.Select,
};

export const AMOUNT_HOME_QUESTION = {
  ...AMOUNT_QUESTION,
  label: 'Insured amount - Home',
  key: 'amountHome',
};

export const AMOUNT_CONTENTS_QUESTION = {
  ...AMOUNT_QUESTION,
  label: 'Insured amount - Contents',
  key: 'amountContents',
};

export const EXCESS_HOME_QUESTION = {
  ...EXCESS_QUESTION,
  label: 'Excess - Home',
  key: 'excessHome',
};

export const EXCESS_CONTENTS_QUESTION = {
  ...EXCESS_QUESTION,
  label: 'Excess - Contents',
  key: 'excessContents',
};

export const PAYMENT_METHOD_QUESTION = {
  basic: true,
  key: 'paymentMethod',
  label: 'Payment method',
  placeholder: 'Select payment method',
  options: PAYMENT_METHODS_OPTIONS,
  type: FieldType.Select,
};

export const POLICY_PROVIDER_QUESTION = {
  basic: true,
  valueKey: 'policyProviderId',
  key: 'policyProviderId',
  label: '',
  options: 'policyProviderId',
  placeholder: 'Select provider',
  type: FieldType.Select,
  hideDisabled: true,
  required: true,
};

export const OTHER_NOTES_FIELD = {
  basic: true,
  key: 'notes',
  label: 'Notes',
  placeholder: 'Enter notes',
  maxLength: 512,
  type: FieldType.Text,
};

export const LIFE_PREMIUM_COVER_FIELD = {
  basic: true,
  key: 'premiumCovers',
  label: 'Premium cover',
  type: FieldType.Select,
  multiple: true,
};

export const LIFE_PREMIUM_COVER_CHECKBOX_FIELD = {
  basic: true,
  key: 'premiumCover',
  label: 'Select if you have “Premium cover”',
  type: FieldType.Checkbox,
  tooltip:
    'An optional benefit you may have selected which covers you for payment of your premiums to keep your insurance in place while you are on claim and may not have the ability to earn an income.',
};

export const LIFE_NAMES_FIELD = {
  basic: true,
  key: 'insuredNames',
  label: 'Name',
  type: FieldType.MultiText,
  placeholder: 'Enter name',
  noResultsMessage: false,
};

export const COPAYMENT_FIELD = {
  basic: true,
  key: 'coPayment',
  label: 'Co-payment',
  type: FieldType.Select,
  options: CO_PAYMENT_OPTIONS,
};

export const LIFE_INSURED_COVERS = {
  basic: true,
  key: 'policyTypes',
  label: 'Insured Covers',
  type: FieldType.Select,
  multiple: true,
  hideDisabled: true,
  required: true,
};

const PAYOUT_FREQUENCY_FIELD = {
  basic: true,
  key: 'payoutFrequency',
  type: FieldType.Select,
  options: PAYOUT_FREQUENCY_OPTIONS,
  label: 'Payout frequency',
  placeholder: 'Select frequency',
  hideDisabled: true,
  tooltip:
    'This is how often the insurer will make a payout to you e.g. monthly, lump sum, etc.',
};

const PAYOUT_WAIT_FIELD = {
  basic: true,
  key: 'payoutWaitPeriod',
  type: FieldType.Select,
  options: PAYOUT_WAIT_OPTIONS,
  label: 'Payout wait period',
  placeholder: 'Select period',
  hideDisabled: true,
  tooltip:
    'This is how many days you must wait, from the date of your illness or injury, before your insurer will start to make a payout to you.',
};

const PAYOUT_BENEFIT_FIELD = {
  basic: true,
  key: 'payoutBenefitPeriod',
  type: FieldType.Select,
  options: PAYOUT_BENEFIT_OPTIONS,
  label: 'Benefit period',
  placeholder: 'Select period',
  hideDisabled: true,
  tooltip:
    'This is the maximum amount of time you can receive the payout as part of your policy.',
};

const LIFE_AMOUNT_FIELD = {
  ...AMOUNT_QUESTION,
  key: 'insuredAmount',
  hideDisabled: true,
};

export const PAYOUT_FIELDS = [
  PAYOUT_FREQUENCY_FIELD,
  PAYOUT_WAIT_FIELD,
  PAYOUT_BENEFIT_FIELD,
];

const COVERS_FIELDS = [LIFE_AMOUNT_FIELD, ...PAYOUT_FIELDS];

export const LIFE_FIELD_MAP = {
  [INSURANCE_TYPES_NAMES.HEALTH]: [
    LIFE_AMOUNT_FIELD,
    { ...EXCESS_QUESTION, hideDisabled: true },
  ],
  [INSURANCE_TYPES_NAMES.INCOME]: COVERS_FIELDS,
  [INSURANCE_TYPES_NAMES.MORTGAGE]: COVERS_FIELDS,
  [INSURANCE_TYPES_NAMES.RENT_PROTECTION]: COVERS_FIELDS,
  default: [LIFE_AMOUNT_FIELD],
};

export const FIELD_BY_POLICY_TYPE_MAP = {
  default: NAME_FIELD,
  [INSURANCE_TYPES_NAMES.MOTORCYCLE]: REGO_FIELD,
  [INSURANCE_TYPES_NAMES.VEHICLE]: REGO_FIELD,
  [INSURANCE_TYPES_NAMES.RV]: REGO_FIELD,
  [INSURANCE_TYPES_NAMES.MBI]: REGO_FIELD,
  [INSURANCE_TYPES_NAMES.CONTENT]: ADDRESS_QUESTION_REQUIRED,
  [INSURANCE_TYPES_NAMES.HOME]: ADDRESS_QUESTION_REQUIRED,
  [INSURANCE_TYPES_NAMES.HOME_CONTENTS]: ADDRESS_QUESTION_REQUIRED,
  [INSURANCE_TYPES_NAMES.LANDLORD]: ADDRESS_QUESTION_REQUIRED,
  [INSURANCE_TYPES_NAMES.RENTERS]: ADDRESS_QUESTION_REQUIRED,
  [INSURANCE_TYPES_NAMES.PET]: PET_NAME_FIELD,
  [INSURANCE_TYPES_NAMES.BOAT]: VESSEL_FIELD,
};
